/**
 * MentalityPanel — the full aside panel rendered when the FAB is open.
 *
 * Composes:
 *   - Header (eyebrow, title, close)
 *   - Module selector
 *   - Loading / error / body (suggested cards + chat thread)
 *   - Composer (owned by MentalityComposer)
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
    Sparkles, X, ChevronDown, AlertTriangle, RefreshCw, Lightbulb,
} from 'lucide-react';
import Button from '../../../core/ui/Button';
import {
    MODULE_LABELS,
    MODULE_ORDER,
    type ModuleKey,
} from '../../../mentorshipService';
import { useMentalityContext } from './useMentalityContext';
import { useMentalityChat } from './useMentalityChat';
import { useMentalityDocument } from './useMentalityDocument';
import { SuggestedCards, ChatBubble } from './Cards';
import { SkeletonOverlay } from '../../../core/ui/skeleton';
import MentalityComposer from './MentalityComposer';
import { riskToCard, insightToCard, profileToCards, type MatchCard } from './adapters';
import { useFocusContext } from '../focus/useFocusContext';
import { useKeyboardScope } from '../../../core/keyboard';
import type { KeyComboShortcut } from '../../../core/keyboard';
import { GLOSSARY } from '../glossary';

type Props = {
    open: boolean;
    onClose: () => void;
};

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Mounts only while the panel is open. Owns Escape (close) + Tab
 *  (focus-trap) shortcuts via the keyboard SSOT. */
function MentalityPanelKeyScope({
    panelRef,
    onClose,
}: {
    panelRef: RefObject<HTMLElement | null>;
    onClose: () => void;
}) {
    const shortcuts = useMemo<ReadonlyArray<KeyComboShortcut>>(
        () => [
            {
                combo: 'escape',
                handler: (e) => {
                    e.stopPropagation();
                    onClose();
                },
            },
            {
                combo: 'tab',
                handler: (e) => {
                    const panel = panelRef.current;
                    if (!panel) return;
                    const current = Array.from(
                        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
                    );
                    if (current.length === 0) return;
                    const first = current[0];
                    const last = current[current.length - 1];
                    if (!first || !last) return;
                    if (e.shiftKey && document.activeElement === first) {
                        e.preventDefault();
                        last.focus();
                    } else if (!e.shiftKey && document.activeElement === last) {
                        e.preventDefault();
                        first.focus();
                    }
                },
            },
        ],
        [panelRef, onClose],
    );
    useKeyboardScope(shortcuts, { owner: 'MentalityPanel' });
    return null;
}

export default function MentalityPanel({ open, onClose }: Props) {
    const [module, setModule] = useState<ModuleKey | ''>('');
    const [query, setQuery] = useState('');

    // a11y: keyboard contract for role="dialog" — replicates the Modal
    // SSOT pattern (Escape to close, Tab trap, focus restoration to the
    // FAB trigger on unmount). Inlined here (not via <Modal>) because
    // the Mentality overlay is a docked aside with its own layout, not
    // a center/right dialog surface.
    const panelRef = useRef<HTMLElement>(null);
    const previouslyFocused = useRef<HTMLElement | null>(null);
    // aria-labelledby target — points at the visible <h2> title so AT
    // users hear the same string sighted users see (and copy edits to
    // the title land in one place, not two).
    const titleId = useId();

    useEffect(() => {
        if (!open) return;

        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const panel = panelRef.current;
        const initial = panel
            ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
            : [];
        initial[0]?.focus();

        return () => {
            previouslyFocused.current?.focus();
        };
    }, [open]);

    // Background-inert snapshot/restore. Mirrors CmdKPalette + HistoryDrawer
    // — snapshot every top-level body sibling's pre-existing inert
    // attribute, mark them inert while the panel is open, and restore
    // verbatim on close. This realises the APG dialog pattern so AT
    // virtual-cursor users can't reach chrome behind the overlay, and
    // a sibling that was already inert for an independent reason (e.g.
    // another overlay) keeps that state when we tear down.
    useEffect(() => {
        if (!open) return;
        const previousInert = new Map<Element, string | null>();
        const panelNode = panelRef.current;
        for (const child of Array.from(document.body.children)) {
            // Skip the subtree that contains the panel itself; marking
            // it inert would also inert the dialog.
            if (panelNode && child.contains(panelNode)) continue;
            previousInert.set(child, child.getAttribute('inert'));
            child.setAttribute('inert', '');
        }
        return () => {
            for (const [child, prior] of previousInert) {
                if (prior === null) child.removeAttribute('inert');
                else child.setAttribute('inert', prior);
            }
        };
    }, [open]);

    // Phase 5a — surface the current FocusContext so the AI coach
    // shows "you're asking about Sarah" instead of generic guidance.
    // WS-SCALE-3 — also thread the focus entity into the context fetch
    // so the backend re-ranks insights/recommendations for the focused
    // mentee. When focus is null we fall back to the global ranking.
    const { focus } = useFocusContext();

    const { context, loading, error, fetchContext, cancelRef } =
        useMentalityContext(open, module, focus);

    const {
        docName, docText, uploading, uploadTruncated, uploadError,
        upload, clear, dismissError,
    } = useMentalityDocument({ open, cancelRef });

    const { chat, sending, send } = useMentalityChat({
        open, context, module, docName, docText, cancelRef,
    });

    const allCards = useMemo<MatchCard[]>(() => {
        if (!context) return [];
        return [
            ...context.risks.map(riskToCard),
            ...context.insights.map(insightToCard),
            ...context.client_profiles.flatMap(profileToCards),
        ];
    }, [context]);

    const hasAnyContent = allCards.length > 0;

    // ── WS-M-5 a11y: live region announcement on re-rank ─────────────
    //
    // The SCALE-3 focus-aware re-rank (shipped 5c3e24d) updates the
    // suggestion set silently when the user pins a different mentee.
    // Screen reader users have no way to perceive the change.
    //
    // We mirror the suggestion-set identity (focus id + card count)
    // into an aria-live='polite' region. Announcements fire only when
    // the identity actually changes — never on every render — so AT
    // users hear "N suggestions ranked for Sarah Chen" exactly once
    // per re-rank, never on incidental re-renders.
    const [liveMessage, setLiveMessage] = useState('');
    const lastAnnouncementRef = useRef<string | null>(null);
    useEffect(() => {
        if (loading || error || !context) return;
        const suggestionCount = allCards.slice(0, 4).length;
        const identity = `${focus?.id ?? '__global__'}::${suggestionCount}`;
        if (lastAnnouncementRef.current === identity) return;
        lastAnnouncementRef.current = identity;
        const next = suggestionCount === 0
            ? ''
            : `${suggestionCount} ${suggestionCount === 1 ? 'suggestion' : 'suggestions'} ranked for ${focus ? focus.label : 'your current context'}.`;
        // Why: this setState mirrors the (focus id, suggestion count)
        // identity into an ARIA live region. The dedupe ref above
        // guarantees the effect runs at most once per re-rank, so this
        // is not a render loop. Mirroring on commit is required: the
        // SCALE-3 focus-aware re-rank (5c3e24d) fires AFTER the focus
        // change settles, and only then do we know the new count to
        // announce. Derived-state-during-render would announce the
        // STALE count for one paint before the next fetch resolves.
        // See docs/mentorship-flow.md "live-region a11y" section.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLiveMessage(next);
    }, [allCards, context, error, focus, loading]);

    const handleSend = () => {
        void send(query).then(() => setQuery(''));
    };

    return (
        <aside
            ref={panelRef}
            className="mentality-overlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
        >
            <MentalityPanelKeyScope panelRef={panelRef} onClose={onClose} />
            <header className="mentality-overlay__head">
                <div className="mentality-overlay__title-group">
                    <span className="mentality-overlay__eyebrow">
                        <Sparkles size={12} aria-hidden="true" /> {GLOSSARY.MENTALITY.shortLabel} overlay
                    </span>
                    <h2 id={titleId} className="mentality-overlay__title">
                        {focus
                            ? `Guidance for ${focus.label}`
                            : 'Situational guidance'}
                    </h2>
                    {focus && (
                        <span className="mentality-overlay__focus-chip">
                            In focus: {focus.label}
                        </span>
                    )}
                </div>
                <button
                    type="button"
                    className="mentality-overlay__close"
                    onClick={onClose}
                    aria-label="Close overlay"
                >
                    <X size={16} aria-hidden="true" />
                </button>
            </header>

            <label className="mentality-overlay__module">
                <span className="mentality-overlay__module-label">Working on</span>
                <div className="mentality-overlay__module-select">
                    <select
                        value={module}
                        onChange={(e) => setModule(e.target.value as ModuleKey | '')}
                    >
                        <option value="">Any document</option>
                        {MODULE_ORDER.map((m) => (
                            <option key={m} value={m}>{MODULE_LABELS[m]}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} aria-hidden="true" />
                </div>
            </label>

            {loading && <SkeletonOverlay lines={4} />}

            {!loading && error && (
                <div className="mentality-overlay__error" role="alert">
                    <AlertTriangle size={14} aria-hidden="true" />
                    {error}
                    <Button
                        variant="ghost"
                        size="sm"
                        icon={<RefreshCw size={12} />}
                        onClick={() => void fetchContext()}
                    >
                        Retry
                    </Button>
                </div>
            )}

            {/* WS-M-5 a11y: polite live region announces re-ranks
              * when the focus context changes. Visually hidden — the
              * suggestion cards remain the primary visual surface.
              * `role="status"` implies `aria-live="polite"` +
              * `aria-atomic="true"` by default; explicit attributes
              * are redundant and at least one older JAWS bug double-
              * announces when both forms are set. Keep role alone. */}
            <div
                className="mentality-overlay__live"
                role="status"
                data-testid="mentality-live-region"
            >
                {liveMessage}
            </div>

            {!loading && !error && context && (
                <div className="mentality-overlay__body">
                    {!hasAnyContent ? (
                        <div className="mentality-overlay__empty">
                            <Lightbulb size={20} />
                            <p>
                                Your mentor hasn't curated any risks or {GLOSSARY.INSIGHT.pluralLabel.toLowerCase()}
                                for this context yet. Check back once your next
                                review lands.
                            </p>
                        </div>
                    ) : (
                        <>
                            <SuggestedCards cards={allCards.slice(0, 4)} />
                            {chat.length > 0 && (
                                <div className="mentality-overlay__chat">
                                    {chat.map((entry) => (
                                        <ChatBubble key={entry.id} entry={entry} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            <footer className="mentality-overlay__foot">
                <MentalityComposer
                    query={query}
                    onQueryChange={setQuery}
                    onSend={handleSend}
                    sending={sending}
                    hasAnyContent={hasAnyContent}
                    docName={docName}
                    docText={docText}
                    uploading={uploading}
                    uploadTruncated={uploadTruncated}
                    uploadError={uploadError}
                    onUpload={upload}
                    onClearDoc={clear}
                    onDismissError={dismissError}
                />
            </footer>
        </aside>
    );
}
